import { api } from '@/app'
import { CategoriesResponse, CategoryDict, ScoreTopic } from '@/types'
import { usePageContext } from '@/utils'
import { AsyncReturnType } from '@codeleap/common'
import { organisationQueryKeys } from './queryKeys'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

type CategoriesFilters = { topic?: string }

async function getOrganisationCategories(filters?: CategoriesFilters) {
  const categoryResponse = await api.get<CategoriesResponse>(`questions/categories/`, { params: filters })
  const subCategoryResponse = await api.get<CategoryDict>(`questions/subcategories/`, { params: filters })

  const data = {
    categories: categoryResponse.data,
    subcategories: subCategoryResponse.data
  }

  return data
}

export type AggregatedScoreCategories = Awaited<ReturnType<typeof getOrganisationCategories>>

export const useCategories = (filters?: CategoriesFilters, enabled?: boolean) => {
  const ctxValue = usePageContext()?.categories

  const query = useQuery({
    queryKey: filters?.topic
      ? organisationQueryKeys.topicCategories.key(filters?.topic)
      : organisationQueryKeys.categories,
    queryFn: () => {
      return getOrganisationCategories(filters)
    },
    enabled
  })

  const data: AsyncReturnType<typeof getOrganisationCategories> = query.data ?? ctxValue

  return {
    query,
    categories: data?.categories,
    subcategories: data?.subcategories
  }
}

async function getTopics() {
  const response = await api.get<ScoreTopic[]>('questions/score_topics/')

  return response.data
}

export function useScoreTopics<T = ScoreTopic[]>(opts?: UseQueryOptions<ScoreTopic[], any, T>) {
  const query = useQuery({
    queryKey: organisationQueryKeys.scoreTopics.key,
    queryFn: getTopics,
    placeholderData: [],
    ...opts
  })

  return query
}

export const useScoreTopic = (topic?: string) => {
  const query = useScoreTopics({
    queryKey: [...organisationQueryKeys.scoreTopics.key, topic],
    select(data) {
      return data?.find(i => i.value === topic)
    },
    enabled: !!topic
  })

  return query
}
